import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, onSnapshot, collection, query, orderBy,
  addDoc, serverTimestamp, updateDoc, deleteDoc, arrayUnion, arrayRemove, increment
} from 'firebase/firestore';

const REACTIONS = [
  { key: 'accepted', emoji: '✅', label: 'Принято' },
  { key: 'done', emoji: '🎯', label: 'Выполнено' },
  { key: 'postponed', emoji: '⏳', label: 'Отложено' },
];

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const formatDateRu = (s) => (s ? s.split('-').reverse().join('.') : '');
const formatDateTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const getHighlight = (post, now) => {
  if (!post.eventDate) return null;
  if (post.eventDate !== toLocalDateStr(now)) return null;
  if (post.eventTime) {
    const [h, m] = post.eventTime.split(':').map(Number);
    const event = new Date(now);
    event.setHours(h, m, 0, 0);
    const diff = now.getTime() - event.getTime();
    if (diff >= -3600000 && diff <= 3600000) return 'red';
  }
  return 'green';
};
const useSmartInput = (text, setText) => {
  const handleChange = (e) => {
    let v = e.target.value;
    if (v.startsWith('... ')) v = 'Уважаемые коллеги! ' + v.slice(4);
    else if (v.startsWith('. ')) v = 'Добрый день! ' + v.slice(2);
    setText(v);
  };
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Голосовой ввод не поддерживается этим браузером. Откройте сайт в Chrome или Edge.'); return; }
    const startRecognition = () => {
      const rec = new SR();
      rec.lang = 'ru-RU';
      rec.continuous = false;
      rec.interimResults = false;
      rec.onresult = (e) => {
        const t = e.results[0][0].transcript;
        setText((prev) => prev ? prev + ' ' + t : t);
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          alert('Нет доступа к микрофону. Нажмите на значок 🔒 слева от адреса сайта и разрешите микрофон.');
        } else {
          alert('Не удалось распознать речь (' + e.error + '). Попробуйте ещё раз.');
        }
      };
      rec.start();
    };
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => { stream.getTracks().forEach((t) => t.stop()); startRecognition(); })
        .catch(() => alert('Нет доступа к микрофону. Разрешите доступ в настройках браузера (🔒 слева от адреса сайта).'));
    } else {
      startRecognition();
    }
  };
  return { handleChange, startVoice };
};

const GroupFeed = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);

  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [text, setText] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [now, setNow] = useState(new Date());
  // ТЕМЫ ГРУППЫ
  const [groupTopics, setGroupTopics] = useState([]);
  const [selectedGroupTopicIds, setSelectedGroupTopicIds] = useState([]);
  const [showGroupTopicForm, setShowGroupTopicForm] = useState(false);
  const [newGroupTopicName, setNewGroupTopicName] = useState('');
  const [editingGroupTopicId, setEditingGroupTopicId] = useState(null);
  const [editGroupTopicName, setEditGroupTopicName] = useState('');

  const fileInputRef = useRef(null);
  const { handleChange: handleTextChange, startVoice } = useSmartInput(text, setText);

  const isAdmin = currentUser?.role === 'admin';
  const isMember = (group?.members || []).includes(currentUser?.uid);
  const isGroupAdmin = isAdmin || (currentUser?.role === 'middle' && isMember);
  const deletedById = {};
  usersList.forEach((u) => { deletedById[u.id] = !!u.deleted; });

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'groups', groupId), (snap) => {
      setGroup(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsubscribe();
  }, [groupId]);

  useEffect(() => {
    const q = query(collection(db, 'groups', groupId, 'posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки ленты группы:', error));
    return () => unsubscribe();
  }, [groupId]);

  // Загрузка тем группы
  useEffect(() => {
    const q = query(collection(db, 'groups', groupId, 'topics'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setGroupTopics(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки тем группы:', error));
    return () => unsubscribe();
  }, [groupId]);

  useEffect(() => {
    const key = 'lastGroupSeen_' + groupId;
    const mark = () => localStorage.setItem(key, String(Date.now()));
    mark();
    const interval = setInterval(mark, 30000);
    return () => clearInterval(interval);
  }, [groupId]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsersList(list);
      const map = {};
      list.forEach((u) => {
        map[u.id] = u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь');
      });
      setUsersById(map);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isGroupAdmin) return;
    const unsubscribe = onSnapshot(collection(db, 'users'), (snap) => {
      setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [isGroupAdmin]);

  const handleFilePick = (file) => {
    if (file.size > 900 * 1024) { alert('Файл слишком большой! Лимит — 900 КБ.'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingFile({ fileName: file.name, fileType: file.type, fileData: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const toggleTag = (id) => {
    setTaggedUsers((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const notifyTagged = async (taggedUids, noteText) => {
    for (const uid of taggedUids) {
      if (uid === currentUser.uid) continue;
      const chatId = [currentUser.uid, uid].sort().join('_');
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        type: 'text',
        text: noteText,
        from: currentUser.uid,
        fromEmail: currentUser.email,
        to: uid,
        read: false,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', uid), {
        [`unreadFrom.${currentUser.uid}`]: increment(1),
      });
    }
  };

  // --- ТЕМЫ ГРУППЫ: управление (только Мидл&Boss / админ) ---
  const toggleGroupTopic = (id) => {
    setSelectedGroupTopicIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleCreateGroupTopic = async () => {
    if (!newGroupTopicName.trim()) return;
    try {
      await addDoc(collection(db, 'groups', groupId, 'topics'), {
        name: newGroupTopicName.trim(),
        createdAt: serverTimestamp(),
      });
      setNewGroupTopicName('');
      setShowGroupTopicForm(false);
    } catch (error) { console.error('Ошибка создания темы:', error); }
  };

  const saveEditGroupTopic = async (topicId) => {
    if (!editGroupTopicName.trim()) return;
    try {
      await updateDoc(doc(db, 'groups', groupId, 'topics', topicId), { name: editGroupTopicName.trim() });
      setEditingGroupTopicId(null);
      setEditGroupTopicName('');
    } catch (error) { console.error('Ошибка переименования темы:', error); }
  };

  const deleteGroupTopic = async (topicId) => {
    if (!window.confirm('Удалить тему? Посты этой темы останутся в группе.')) return;
    try {
      await deleteDoc(doc(db, 'groups', groupId, 'topics', topicId));
      setSelectedGroupTopicIds((prev) => prev.filter((id) => id !== topicId));
    } catch (error) { console.error('Ошибка удаления темы:', error); }
  };

  const groupTopicNameById = (id) => {
    const t = groupTopics.find((t) => t.id === id);
    return t ? t.name : null;
  };

  const postTopicId = selectedGroupTopicIds.length > 0 ? selectedGroupTopicIds[0] : null;

  const handlePost = async () => {
    if (!text.trim() && !pendingFile) return;
    try {
      await addDoc(collection(db, 'groups', groupId, 'posts'), {
        text: text.trim(),
        topicId: postTopicId,
        fileName: pendingFile ? pendingFile.fileName : null,
        fileType: pendingFile ? pendingFile.fileType : null,
        fileData: pendingFile ? pendingFile.fileData : null,
        eventDate: eventDate || null,
        eventTime: eventDate && eventTime ? eventTime : null,
        authorId: currentUser.uid,
        authorEmail: currentUser.email,
        authorName: currentUser.displayName || currentUser.email,
        groupId: groupId,
        taggedUsers: taggedUsers,
        createdAt: serverTimestamp(),
      });

      if (taggedUsers.length > 0) {
        const authorName = currentUser.displayName || currentUser.email;
        const dateSuffix =
          (eventDate ? ` 📅 ${eventDate.split('-').reverse().join('.')}` : '') +
          (eventDate && eventTime ? ` ⏰ ${eventTime}` : '');
        await notifyTagged(
          taggedUsers,
          `🔖 ${authorName} отметил(а) вас в посте группы «${group.name}»: «${text.trim()}»${dateSuffix}`
        );
      }

      setText('');
      setPendingFile(null);
      setEventDate('');
      setEventTime('');
      setTaggedUsers([]);
      setShowTagPicker(false);
    } catch (error) {
      console.error('Ошибка публикации в группу:', error);
    }
  };

  const startEdit = (p) => {
    setEditingPostId(p.id);
    setEditText(p.text);
    setEditDate(p.eventDate || '');
    setEditTime(p.eventTime || '');
  };
  const saveEdit = async (p) => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, 'groups', groupId, 'posts', p.id), {
        text: editText.trim(),
        edited: true,
        eventDate: editDate || null,
        eventTime: editDate && editTime ? editTime : null,
      });
      setEditingPostId(null);
      setEditText('');
      setEditDate('');
      setEditTime('');
    } catch (error) {
      console.error('Ошибка редактирования:', error);
    }
  };

  const deleteGroupPost = async (p) => {
    if (!window.confirm('Удалить пост из группы?')) return;
    try {
      await deleteDoc(doc(db, 'groups', groupId, 'posts', p.id));
    } catch (error) {
      console.error('Ошибка удаления поста:', error);
    }
  };

  const handleReact = async (post, key) => {
    try {
      const ref = doc(db, 'groups', groupId, 'posts', post.id);
      const reactions = post.reactions || {};
      const current = Object.keys(reactions).find((k) => (reactions[k] || []).includes(currentUser.uid));
      const updates = {};
      if (current === key) {
        updates[`reactions.${key}`] = arrayRemove(currentUser.uid);
      } else {
        updates[`reactions.${key}`] = arrayUnion(currentUser.uid);
        if (current) updates[`reactions.${current}`] = arrayRemove(currentUser.uid);
      }
      await updateDoc(ref, updates);
    } catch (error) {
      console.error('Ошибка реакции:', error);
    }
  };

  const toggleMember = async (uid) => {
    const ref = doc(db, 'groups', groupId);
    const members = group?.members || [];
    try {
      if (members.includes(uid)) await updateDoc(ref, { members: arrayRemove(uid) });
      else await updateDoc(ref, { members: arrayUnion(uid) });
    } catch (error) {
      console.error('Ошибка изменения состава группы:', error);
    }
  };

  if (!group) return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Группа не найдена.</div>;

  if (!isMember && !isAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ fontSize: '40px', margin: '0 0 10px 0' }}>🔒</p>
        <p>Эта группа закрыта. Доступ только для участников.</p>
        <button onClick={() => navigate('/groups')} style={{ padding: '10px 20px', background: '#4a90e2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>← К списку групп</button>
      </div>
    );
  }

  const filteredPosts = posts
    .filter((p) => selectedGroupTopicIds.length === 0 || selectedGroupTopicIds.includes(p.topicId))
    .filter((p) => {
      const q = searchText.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.text || '').toLowerCase().includes(q) ||
        (p.authorName || '').toLowerCase().includes(q) ||
        (p.authorEmail || '').toLowerCase().includes(q) ||
        (p.fileName || '').toLowerCase().includes(q)
      );
    });

  const chipStyle = (active, isPlus) => ({
    flex: '0 0 auto', padding: '6px 12px', borderRadius: '16px', border: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap',
    background: isPlus ? '#4caf50' : active ? '#2a5298' : '#fff',
    color: active || isPlus ? '#fff' : '#333',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  });
  const tinyBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: '11px', padding: '0 2px', lineHeight: 1, opacity: 0.85,
  };

  return (
    <div style={{ padding: '20px' }}>
      <button onClick={() => navigate('/groups')} style={{ background: 'transparent', border: 'none', color: '#4a90e2', cursor: 'pointer', fontSize: '15px', marginBottom: '15px' }}>← Все группы</button>

      <div style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.4)', padding: '20px', borderRadius: '10px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0 }}>👥 {group.name}</h2>
          {isGroupAdmin && (
            <button onClick={() => setShowMembers(!showMembers)} style={{ padding: '8px 14px', background: showMembers ? '#ccc' : '#f0f2f5', color: '#333', border: 'none', borderRadius: '20px', cursor: 'pointer', fontWeight: 600 }}>
              {showMembers ? '✖ Скрыть' : '⚙️ Участники'}
            </button>
          )}
        </div>
        {group.description && <p style={{ margin: '8px 0 0 0', color: '#666' }}>{group.description}</p>}
        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#888' }}>Участников: {(group.members || []).length}</p>
      </div>

      {isGroupAdmin && showMembers && (
        <div style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.4)', padding: '15px', borderRadius: '10px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', maxHeight: '320px', overflowY: 'auto' }}>
          <h4 style={{ margin: '0 0 10px 0' }}>Кто входит в группу:</h4>
          <input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="🔍 Поиск по имени или email..."
            style={{ width: '100%', padding: '8px 12px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '16px', boxSizing: 'border-box', background: '#fff', fontSize: '13px' }}
          />
          {allUsers
            .filter((u) => {
              const q = memberSearch.trim().toLowerCase();
              if (!q) return true;
              return (
                (u.displayName || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q)
              );
            })
            .map((u) => (
              <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                <input type="checkbox" checked={(group.members || []).includes(u.id)} onChange={() => toggleMember(u.id)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                <span style={{ fontSize: '14px' }}>
                  {u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь')}
                  <span style={{ color: '#999', fontSize: '11px' }}> · {u.email}</span>
                </span>
              </label>
            ))}
        </div>
      )}

      {/* ТЕМЫ ГРУППЫ */}
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '3px 0 8px 0' }}>
          <button onClick={() => setSelectedGroupTopicIds([])} style={chipStyle(selectedGroupTopicIds.length === 0, false)}>📋 Все</button>
          {groupTopics.map((t) => {
            const isSelected = selectedGroupTopicIds.includes(t.id);
            if (isGroupAdmin && editingGroupTopicId === t.id) {
              return (
                <span key={t.id} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flex: '0 0 auto' }}>
                  <input value={editGroupTopicName} onChange={(e) => setEditGroupTopicName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && saveEditGroupTopic(t.id)} autoFocus
                    style={{ width: '110px', padding: '5px 8px', borderRadius: '12px', border: '1px solid #2a5298', fontSize: '13px' }} />
                  <button onClick={() => saveEditGroupTopic(t.id)} style={{ ...tinyBtn, fontSize: '14px' }}>✅</button>
                  <button onClick={() => setEditingGroupTopicId(null)} style={{ ...tinyBtn, fontSize: '14px' }}>✕</button>
                </span>
              );
            }
            return (
              <span key={t.id} onClick={() => toggleGroupTopic(t.id)} title="Можно выбрать несколько тем"
                style={{ ...chipStyle(isSelected, false), display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                # {t.name}
                {isGroupAdmin && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); setEditingGroupTopicId(t.id); setEditGroupTopicName(t.name); }} title="Переименовать тему" style={tinyBtn}>✏️</button>
                    <button onClick={(e) => { e.stopPropagation(); deleteGroupTopic(t.id); }} title="Удалить тему" style={tinyBtn}>🗑️</button>
                  </>
                )}
              </span>
            );
          })}
          {isGroupAdmin && (
            <button onClick={() => setShowGroupTopicForm(!showGroupTopicForm)} style={chipStyle(false, true)} title="Создать тему">＋</button>
          )}
        </div>
        {isGroupAdmin && showGroupTopicForm && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            <input value={newGroupTopicName} onChange={(e) => setNewGroupTopicName(e.target.value)} placeholder="Название темы"
              style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '13px' }}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateGroupTopic()} />
            <button onClick={handleCreateGroupTopic} style={{ padding: '8px 12px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Создать</button>
          </div>
        )}
      </div>

      <input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="🔍 Поиск по сообщениям группы..."
        style={{ width: '100%', padding: '10px 15px', marginBottom: '15px', border: '1px solid #ddd', borderRadius: '20px', boxSizing: 'border-box', background: 'rgba(255,255,255,0.8)' }}
      />

      {currentUser?.blocked && (
        <div style={{ background: '#ffebee', color: '#c62828', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '15px' }}>
          🚫 Вы заблокированы — доступен только просмотр
        </div>
      )}

      {isMember && !currentUser?.blocked && (
        <div style={{ background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.5)', padding: '15px', borderRadius: '10px', marginBottom: '20px' }}>
          <textarea value={text} onChange={handleTextChange}
            placeholder={`Написать в группу «${group.name}»...`}
            style={{ width: '100%', height: '70px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />

          {pendingFile && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '4px 12px', background: '#f0f2f5', borderRadius: '15px', fontSize: '13px' }}>
              📎 {pendingFile.fileName}
              <button onClick={() => setPendingFile(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>📅 Дата:</span>
            <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px' }} />
            <span style={{ fontSize: '12px', color: '#888' }}>⏰ Время:</span>
            <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} disabled={!eventDate}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px', opacity: eventDate ? 1 : 0.5 }} />
            {(eventDate || eventTime) && (
              <button onClick={() => { setEventDate(''); setEventTime(''); }} title="Сбросить дату и время"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowTagPicker(!showTagPicker)}
              style={{ padding: '4px 10px', background: showTagPicker ? '#2a5298' : '#f0f2f5', color: showTagPicker ? '#fff' : '#333', border: 'none', borderRadius: '14px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              👤 Отметить
            </button>
            {taggedUsers.map((id) => (
              <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e3f2fd', color: '#1e3c72', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                {usersById[id] || '—'}
                <button onClick={() => toggleTag(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700, fontSize: '12px' }}>✕</button>
              </span>
            ))}
          </div>
          {showTagPicker && (
            <div style={{ border: '1px solid #ddd', borderRadius: '8px', maxHeight: '140px', overflowY: 'auto', background: '#fff', padding: '6px', marginTop: '6px' }}>
              {usersList.filter((u) => u.id !== currentUser?.uid && !u.deleted).map((u) => (
                <label key={u.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={taggedUsers.includes(u.id)} onChange={() => toggleTag(u.id)} />
                  <span style={{ fontSize: '13px' }}>{u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь')}</span>
                </label>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', alignItems: 'center' }}>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files[0]; if (f) handleFilePick(f); e.target.value = ''; }} />
            <button onClick={() => fileInputRef.current && fileInputRef.current.click()} title="Прикрепить файл (до 900 КБ)"
              style={{ padding: '10px 14px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' }}>📎</button>
            {window.innerWidth > 768 && (
              <button onClick={startVoice} title="Ввести голосом (только на компьютере)"
                style={{ padding: '10px 14px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' }}>🎤</button>
            )}
            <button onClick={handlePost} disabled={!text.trim() && !pendingFile}
              style={{ padding: '10px 20px', background: !text.trim() && !pendingFile ? '#ccc' : '#4a90e2', color: '#fff', border: 'none', borderRadius: '5px', cursor: !text.trim() && !pendingFile ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
              Опубликовать в группе
            </button>
          </div>
          <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#888' }}>
            Тема: {postTopicId ? '# ' + groupTopicNameById(postTopicId) : 'без темы'}
          </p>
        </div>
      )}

      {searchText.trim() && (
        <p style={{ fontSize: '13px', color: '#888', margin: '0 0 10px 0' }}>Найдено: {filteredPosts.length}</p>
      )}
      {filteredPosts.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888' }}>{searchText.trim() ? 'Ничего не найдено.' : 'В группе пока нет сообщений.'}</p>
      ) : (
        filteredPosts.map((p) => {
          const highlight = getHighlight(p, now);
          const textStyle =
            highlight === 'green'
              ? { color: '#2e7d32', background: 'rgba(76,175,80,0.15)', padding: '4px 8px', borderRadius: '6px' }
              : highlight === 'red'
              ? { color: '#c62828', background: 'rgba(244,67,54,0.18)', padding: '4px 8px', borderRadius: '6px', fontWeight: 700 }
              : {};
          return (
            <div key={p.id} style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.5)', padding: '15px', borderRadius: '10px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#4a90e2' }}>{deletedById[p.authorId] ? '[удалённый пользователь]' : (p.authorName || (p.authorEmail ? p.authorEmail.split('@')[0] : 'Аноним'))}</strong>
                  {groupTopicNameById(p.topicId) && (
                    <span style={{ background: '#e3f2fd', color: '#1e3c72', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
                      # {groupTopicNameById(p.topicId)}
                    </span>
                  )}
                  <span style={{ fontSize: '11px', color: '#999' }}>{formatDateTime(p.createdAt)}</span>
                  {(p.eventDate || p.eventTime) && (
                    <span style={{
                      background: highlight === 'red' ? '#ffebee' : highlight === 'green' ? '#e8f5e9' : '#f0f2f5',
                      color: highlight === 'red' ? '#c62828' : highlight === 'green' ? '#2e7d32' : '#666',
                      padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                    }}>
                      📅 {formatDateRu(p.eventDate)}{p.eventTime ? ` ⏰ ${p.eventTime}` : ''}
                    </span>
                  )}
                </div>
                <span>
                  {!currentUser?.blocked && p.authorId === currentUser?.uid && editingPostId !== p.id && (
                    <button onClick={() => startEdit(p)} title="Редактировать (текст, дата, время)"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: 0.8 }}>✏️</button>
                  )}
                  {!currentUser?.blocked && (p.authorId === currentUser?.uid || isGroupAdmin) && (
                    <button onClick={() => deleteGroupPost(p)} title="Удалить пост"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: 0.8, color: '#ff4d4f' }}>🗑️</button>
                  )}
                </span>
              </div>

              {(p.taggedUsers || []).length > 0 && (
                <div style={{ fontSize: '12px', color: '#2a5298', marginTop: '4px', fontWeight: 600 }}>
                  👤 Отмечены: {p.taggedUsers.map((id) => usersById[id] || '—').join(', ')}
                </div>
              )}

              {editingPostId === p.id ? (
                <div style={{ marginTop: '8px' }}>
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                    style={{ width: '100%', height: '70px', padding: '8px', border: '1px solid #2a5298', borderRadius: '5px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '14px' }} />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#888' }}>📅 Дата:</span>
                    <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px' }} />
                    <span style={{ fontSize: '12px', color: '#888' }}>⏰ Время:</span>
                    <input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} disabled={!editDate}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px', opacity: editDate ? 1 : 0.5 }} />
                    {(editDate || editTime) && (
                      <button onClick={() => { setEditDate(''); setEditTime(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    <button onClick={() => saveEdit(p)} style={{ padding: '5px 12px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>✅ Сохранить</button>
                    <button onClick={() => { setEditingPostId(null); setEditText(''); setEditDate(''); setEditTime(''); }} style={{ padding: '5px 12px', background: '#ccc', color: '#333', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>Отмена</button>
                  </div>
                </div>
              ) : (
                <>
                  {p.text && (
                    <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5, ...textStyle }}>
                      {p.text}
                      {p.edited && <em style={{ fontSize: '11px', color: '#999' }}> (изменено)</em>}
                    </p>
                  )}
                  {p.fileData && (
                    <a href={p.fileData} download={p.fileName}
                      style={{ display: 'inline-block', marginTop: '8px', padding: '8px 14px', background: '#f0f2f5', borderRadius: '8px', color: '#4a90e2', fontWeight: 600, textDecoration: 'none' }}>
                      📥 {p.fileName}
                    </a>
                  )}
                </>
              )}

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px', flexWrap: 'wrap' }}>
                {!currentUser?.blocked && REACTIONS.map((r) => {
                  const uids = (p.reactions || {})[r.key] || [];
                  const active = uids.includes(currentUser?.uid);
                  return (
                    <button key={r.key} onClick={() => handleReact(p, r.key)}
                      title={r.label + (uids.length ? ': ' + uids.map((id) => usersById[id] || '?').join(', ') : '')}
                      style={{
                        background: active ? '#e3f2fd' : '#f5f5f5',
                        border: active ? '1px solid #2a5298' : '1px solid transparent',
                        borderRadius: '12px', padding: '2px 8px', cursor: 'pointer',
                        fontSize: '12px', fontWeight: active ? 700 : 400,
                        color: active ? '#1e3c72' : '#666',
                      }}>
                      {r.emoji}{uids.length > 0 ? ` ${uids.length}` : ''}
                    </button>
                  );
                })}
              </div>

              {REACTIONS.some((r) => ((p.reactions || {})[r.key] || []).length > 0) && (
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.5 }}>
                  {REACTIONS.map((r) => {
                    const uids = (p.reactions || {})[r.key] || [];
                    if (uids.length === 0) return null;
                    return (
                      <span key={r.key} style={{ marginRight: '10px' }}>
                        {r.emoji} {uids.map((id) => usersById[id] || '—').join(', ')}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default GroupFeed;